import { Fragment, type ReactNode } from "react";

/**
 * Beyond Sure — minimal Claude-style markdown renderer (zero deps).
 *
 * Supports: paragraphs, **bold**, `inline code`, [link](url),
 * "- " bullets, "1. " numbered lists, ```fenced``` code, "#"/"##"/"###"
 * headings, "---" horizontal rules, and GFM-style "| a | b |" tables
 * (with an inline auto-chart for numeric columns — see TableBlock below).
 *
 * AZ fix: the advisor's real responses reliably contain headings, hr
 * dividers, and comparison tables (verified live this session — e.g.
 * "## Term Insurance vs ULIP", "---", "| Age | Premium | ... |"), but
 * this renderer previously had NO block type for any of the three —
 * they fell into the plain-paragraph fallback and rendered as literal
 * "##"/"|"-cluttered text. This was a real, verified functional gap in
 * the (not-yet-live, LAUNCH2_UI-gated) React chat surface.
 *
 * Security invariant preserved: this renderer builds React elements
 * directly (never raw-HTML string injection), so pasted markup from a
 * user or the model can never execute as script.
 */

interface MarkdownProps {
  source: string;
}

export function Markdown({ source }: MarkdownProps) {
  const blocks = splitBlocks(source);
  return (
    <div className="bsure-prose space-y-3 text-[15px] leading-[1.65] text-[var(--color-ink)]">
      {blocks.map((b, i) => renderBlock(b, i))}
    </div>
  );
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "code"; body: string }
  | { kind: "h"; level: 1 | 2 | 3 | 4; text: string }
  | { kind: "hr" }
  | { kind: "table"; header: string[]; rows: string[][] };

const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const HEADING_RE = /^(#{1,4})\s+(.*)$/;
// A table row: at least one "|" with non-trivial content on both sides.
const TABLE_ROW_RE = /^\s*\|?(.+\|.+)\|?\s*$/;
// The GFM separator row, e.g. "|---|:---:|---:|" or "---|---".
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function splitBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      i++;
      continue;
    }

    if (line.trim().startsWith("```")) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        body.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++;
      blocks.push({ kind: "code", body: body.join("\n") });
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }

    const hMatch = line.match(HEADING_RE);
    if (hMatch) {
      blocks.push({
        kind: "h",
        level: hMatch[1].length as 1 | 2 | 3 | 4,
        text: hMatch[2].trim(),
      });
      i++;
      continue;
    }

    // Table: current line looks like a row AND the next line is a GFM
    // separator ("|---|---|"). Requires the lookahead so a stray "a | b"
    // sentence never misfires as a table.
    if (
      TABLE_ROW_RE.test(line) &&
      i + 1 < lines.length &&
      TABLE_SEP_RE.test(lines[i + 1])
    ) {
      const header = splitTableRow(line);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (i < lines.length && TABLE_ROW_RE.test(lines[i]) && lines[i].trim() !== "") {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }

    if (/^\s*-\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }

    const pLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !HR_RE.test(lines[i]) &&
      !HEADING_RE.test(lines[i]) &&
      !/^\s*-\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !lines[i].trim().startsWith("```") &&
      !(TABLE_ROW_RE.test(lines[i]) && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]))
    ) {
      pLines.push(lines[i]);
      i++;
    }
    blocks.push({ kind: "p", lines: pLines });
  }
  return blocks;
}

const HEADING_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: "text-[22px] font-semibold tracking-[-0.01em] text-[var(--color-ink)] mt-1",
  2: "text-[18px] font-semibold tracking-[-0.01em] text-[var(--color-ink)] mt-1",
  3: "text-[15.5px] font-semibold text-[var(--color-ink)]",
  4: "text-[13.5px] font-semibold uppercase tracking-[0.08em] text-[var(--color-ink-60)]",
};

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case "p":
      return (
        <p key={key} className="text-[15px] leading-[1.65]">
          {block.lines.map((ln, j) => (
            <Fragment key={j}>
              {renderInline(ln)}
              {j < block.lines.length - 1 ? <br /> : null}
            </Fragment>
          ))}
        </p>
      );
    case "ul":
      return (
        <ul
          key={key}
          className="list-disc pl-5 space-y-1.5 marker:text-[var(--color-brand-deep)]"
        >
          {block.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol
          key={key}
          className="list-decimal pl-5 space-y-1.5 marker:text-[var(--color-brand-deep)] marker:font-semibold"
        >
          {block.items.map((it, j) => (
            <li key={j}>{renderInline(it)}</li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre
          key={key}
          className={[
            "rounded-[var(--radius-md)] px-4 py-3 overflow-x-auto",
            "bg-[rgb(20_28_24_/_0.06)] border border-[var(--color-ink-08)]",
            "text-[13px] font-mono leading-[1.55] text-[var(--color-ink)]",
          ].join(" ")}
        >
          <code>{block.body}</code>
        </pre>
      );
    case "hr":
      return (
        <hr key={key} className="border-t border-[var(--color-ink-08)] my-1" />
      );
    case "h":
      return (
        <div key={key} className={HEADING_CLASS[block.level]}>
          {renderInline(block.text)}
        </div>
      );
    case "table":
      return <TableBlock key={key} header={block.header} rows={block.rows} />;
  }
}

/** Parses "₹1,50,000" / "1.5Cr" / "12,450" / "24%" style cells into a plain number, or null if non-numeric. */
function parseNumericCell(raw: string): number | null {
  const s = raw.trim();
  if (!s || s === "—" || s === "-") return null;
  // Reject ranges/multi-value cells ("₹8,000–11,000") — a chart needs one value per cell.
  if (/[–—]|(\bto\b)/i.test(s)) return null;
  let mult = 1;
  if (/cr\b/i.test(s)) mult = 1e7;
  else if (/lakh|\bl\b/i.test(s)) mult = 1e5;
  const cleaned = s.replace(/[₹,%\s]/g, "").replace(/(cr|lakh|l)$/i, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n * mult : null;
}

/**
 * Renders a GFM table with a Stripe/Linear-style layout, and — when a
 * column is reliably numeric across every row — an inline bar visual
 * per cell (reusing the same visual language as ComparisonBlock's
 * FitBar) so the reader gets an at-a-glance "chart" without the model
 * needing a separate structured-data contract. Chart is opt-out via
 * data density: needs 2+ rows and every cell in the column parseable.
 */
function TableBlock({ header, rows }: { header: string[]; rows: string[][] }) {
  const colCount = header.length;
  const numericCols = new Set<number>();
  if (rows.length >= 2) {
    for (let c = 1; c < colCount; c++) {
      // Skip column 0 — it's almost always the label (insurer/age/plan), never charted.
      const vals = rows.map((r) => parseNumericCell(r[c] ?? ""));
      if (vals.every((v) => v != null)) numericCols.add(c);
    }
  }
  const maxByCol = new Map<number, number>();
  numericCols.forEach((c) => {
    const max = Math.max(...rows.map((r) => parseNumericCell(r[c] ?? "") ?? 0), 1);
    maxByCol.set(c, max);
  });

  return (
    <div
      data-testid="message-table"
      className="rounded-[12px] border border-[var(--color-ink-08)] overflow-hidden overflow-x-auto"
    >
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[var(--color-ink-04,rgba(20,28,24,0.04))] border-b border-[var(--color-ink-08)]">
            {header.map((h, j) => (
              <th
                key={j}
                className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-ink-60)] px-3 py-2 whitespace-nowrap"
              >
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-[var(--color-ink-08)] last:border-b-0"
            >
              {header.map((_, ci) => {
                const cell = row[ci] ?? "";
                const isChartCol = numericCols.has(ci);
                const val = isChartCol ? parseNumericCell(cell) : null;
                const max = maxByCol.get(ci) ?? 1;
                return (
                  <td
                    key={ci}
                    className="px-3 py-2 text-[13.5px] text-[var(--color-ink)] align-middle"
                  >
                    {isChartCol && val != null ? (
                      <div className="flex items-center gap-2 min-w-[90px]">
                        <span className="tabular-nums whitespace-nowrap">
                          {renderInline(cell)}
                        </span>
                        <span className="relative flex-1 h-[5px] rounded-full overflow-hidden bg-[var(--color-ink-08)] min-w-[36px]">
                          <span
                            className="absolute inset-y-0 left-0 rounded-full"
                            style={{
                              width: `${Math.max(4, Math.min(100, (val / max) * 100))}%`,
                              background:
                                "linear-gradient(90deg, var(--color-brand-deep), var(--color-brand) 60%, var(--color-brand-vivid))",
                            }}
                          />
                        </span>
                      </div>
                    ) : (
                      renderInline(cell)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const INLINE_PATTERN =
  /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const idx = match.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = match[0];
    if (tok.startsWith("`")) {
      out.push(
        <code
          key={`c${key++}`}
          className="rounded-[6px] bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)] px-[6px] py-[1px] font-mono text-[13.5px]"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("**")) {
      out.push(
        <strong
          key={`b${key++}`}
          className="font-semibold text-[var(--color-ink)]"
        >
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("[")) {
      const m = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (m) {
        out.push(
          <a
            key={`l${key++}`}
            href={m[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-brand-deep)] underline decoration-[var(--color-brand)] underline-offset-2 hover:text-[var(--color-brand)]"
          >
            {m[1]}
          </a>,
        );
      } else {
        out.push(tok);
      }
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
